if (!customElements.get('wishlist-section')) {
  class WishlistSection extends HTMLElement {
    constructor() {
      super();
      this.customerType = window.analyzify?.shopify_customer.type || 'visitor';
      this.customerId = this.customerType === 'member' ? window.analyzify?.shopify_customer.id : 'visitor';
      this.moneyFormat = this.dataset.moneyFormat || '{{ shop.money_format }}';
    }

    connectedCallback() {
      this._initializeEvents();
      this._subscribeToWishlistChanges();
      const wishlist = localStorage.getItem('wishlist');
      wishlist ? this.getWishlistProducts() : this.handleEmptyWishlist();
    }

    // Event Listeners
    _initializeEvents() {
      this.addEventListener('click', this._handleClick.bind(this));
      this.addEventListener('change', this._handleChange.bind(this));
    }

    _handleClick(event) {
      const quantityBtn = event.target.closest('.quantity-btn');
      const addToCartBtn = event.target.closest('.add-to-cart-btn');
      const removeBtn = event.target.closest('.wishlist-button-table');

      if (quantityBtn) this._handleQuantityChange(quantityBtn);
      else if (addToCartBtn) this._handleAddToCart(addToCartBtn, event);
      else if (removeBtn) this._handleRemoveItem(removeBtn);
    }

    _handleChange(event) {
      const variantSelect = event.target.closest('.product-variant-select');
      if (variantSelect) this._onVariantChange(variantSelect);
    }

    _subscribeToWishlistChanges() {
      window.pubSub?.subscribe('wishlist:updated', () => this.refreshWishlist());
      window.pubSub?.subscribe('wishlist:removed', () => this.refreshWishlist());
    }

    // Storage Helpers
    _getStorageData(key) {
      try {
        return JSON.parse(localStorage.getItem(key)) || {};
      } catch {
        return {};
      }
    }

    _saveStorageData(key, handle, value) {
      const data = this._getStorageData(key);
      data[handle] = value;
      localStorage.setItem(key, JSON.stringify(data));
    }

    _removeStorageData(key, handle) {
      const data = this._getStorageData(key);
      delete data[handle];
      localStorage.setItem(key, JSON.stringify(data));
    }

    // Wishlist Management
    refreshWishlist() {
      const wrapper = this.querySelector('.js-wishlist-wrapper');
      wrapper?.classList.add('is-loading');
      
      const wishlist = localStorage.getItem('wishlist');
      wishlist ? this.getWishlistProducts() : this.handleEmptyWishlist();
    }

    async getWishlistProducts() {
      const wrapper = this.querySelector('.js-wishlist-wrapper');
      const handles = localStorage.getItem('wishlist')?.split('||').reverse().filter(Boolean) || [];
      
      if (!handles.length) {
        this.handleEmptyWishlist();
        return;
      }

      const htmlRows = await this._fetchProductRows(handles);
      
      handles.length >= 4 
        ? this._renderGroupedProducts(wrapper, htmlRows)
        : this._renderSimpleProductList(wrapper, htmlRows);

      this.querySelectorAll('.js-wishlist-item').forEach(row => this._applySavedSettings(row));
      
      wrapper?.classList.remove('is-loading');
      this.pushWishlistViewEvent();
      this._calculateAndPublishTotal();
    }

    async _fetchProductRows(handles) {
      const promises = handles.map(handle =>
        fetch(`/products/${handle}?view=wishlist-row`)
          .then(res => res.ok ? res.text() : null)
      );
      return (await Promise.all(promises)).filter(Boolean);
    }

    _renderGroupedProducts(wrapper, htmlRows) {
      const headerTemplate = this.querySelector('#wishlist-table-header');
      const parser = new DOMParser();
      const groupedByType = {};

      htmlRows.forEach(html => {
        const doc = parser.parseFromString(`<table><tbody>${html}</tbody></table>`, 'text/html');
        const productRow = doc.querySelector('tr');
        const type = productRow?.dataset.productType || 'Other';
        (groupedByType[type] ||= []).push(html);
      });

      let finalHTML = '';
      const ungrouped = [];

      Object.entries(groupedByType).forEach(([type, products]) => {
        if (products.length >= 3) {
          const header = headerTemplate.content.cloneNode(true);
          const table = header.querySelector('.wishlist-table');
          
          header.querySelector('.wishlist-th--product').textContent = `${type} (${products.length})`;
          // ['variant', 'amount', 'actions'].forEach(cls => {
          //   header.querySelector(`.wishlist-th--${cls}`).textContent = '';
          // });
          
          table.classList.add('is-sub-group');
          header.querySelector('tbody').innerHTML = products.join('');
          finalHTML += `<div class="wishlist-group">${table.outerHTML}</div>`;
        } else {
          ungrouped.push(...products);
        }
      });

      if (ungrouped.length) {
        let html = headerTemplate.innerHTML.replace(
          '<!-- Product lines will be added here -->',
          ungrouped.join('')
        );
        finalHTML += `<div class="wishlist-group">${html}</div>`;
      }

      wrapper.innerHTML = finalHTML;
    }

    _renderSimpleProductList(wrapper, htmlRows) {
      const html = this.querySelector('#wishlist-table-header').innerHTML.replace(
        '<!-- Product lines will be added here -->',
        htmlRows.join('')
      );
      wrapper.innerHTML = html;
    }

    handleEmptyWishlist() {
      this.querySelector('.js-wishlist-container')?.classList.add('hidden');
      this.querySelector('.js-wishlist-empty')?.classList.remove('hidden');
    }

    // Item Actions
    _handleRemoveItem(btn) {
      const row = btn.closest('tr.js-wishlist-item');
      if (!row) return;

      const handle = row.dataset.productHandle;
      ['wishlist_quantities', 'wishlist_variants'].forEach(key => 
        this._removeStorageData(key, handle)
      );

      row.style.cssText = 'opacity: 0; transition: opacity 0.3s ease';
      setTimeout(() => {
        row.remove();
        window.pubSub?.publish('wishlist:updated');
        if (!this.querySelector('.js-wishlist-wrapper tr')) {
          this.handleEmptyWishlist();
        }
        this._calculateAndPublishTotal();
        this.pushWishlistViewEvent();
      }, 300);
    }

    _handleQuantityChange(btn) {
      const row = btn.closest('tr.js-wishlist-item');
      if (!row) return;

      const input = row.querySelector('.quantity-input');
      let qty = parseInt(input.value, 10);
      
      if (btn.dataset.action === 'plus') qty++;
      else if (btn.dataset.action === 'minus' && qty > 1) qty--;

      input.value = qty;
      this._saveStorageData('wishlist_quantities', row.dataset.productHandle, qty);
      this._updatePrice(row);
      this._calculateAndPublishTotal();
    }

    _handleAddToCart(btn, event) {
      event.preventDefault();
      const row = btn.closest('tr.js-wishlist-item');
      if (!row) return;

      const variantId = btn.dataset.variantId;
      const qty = parseInt(row.querySelector('.quantity-input').value, 10);

      if (variantId && qty) this._addProductToCart(variantId, qty);
    }

    async _addProductToCart(variantId, quantity) {
      try {
        const cartAddUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';
        const response = await fetch(`${cartAddUrl}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id: variantId, quantity }],
            sections: 'cart-drawer',
            sections_url: window.location.pathname
          })
        });

        const data = await response.json();
        
        if (data.status === 'bad_request') {
          console.error('Error adding to cart:', data.description);
          return;
        }

        if (typeof publishEvent === 'function' && window.PUB_SUB_EVENTS) {
          publishEvent(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', data });
        }
      } catch (error) {
        console.error('Error adding product to cart:', error);
      }
    }

    // Variant Management
    _applySavedSettings(row) {
      const handle = row.dataset.productHandle;
      const quantities = this._getStorageData('wishlist_quantities');
      const savedVariants = this._getStorageData('wishlist_variants');
      
      if (quantities[handle]) {
        row.querySelector('.quantity-input').value = quantities[handle];
      }

      const savedVariantId = savedVariants[handle];
      if (savedVariantId) {
        const variantsJson = row.querySelector('.productVariantsJson');
        if (!variantsJson) return;
        
        const variants = JSON.parse(variantsJson.textContent);
        const selectedVariant = variants.find(v => v.id == savedVariantId);
        
        if (selectedVariant) {
          row.querySelectorAll('.product-variant-select').forEach((select, index) => {
            select.value = selectedVariant.options[index];
            this._updateCustomSelectDisplay(select);
          });
          this._updateVariantDetails(row, selectedVariant, false);
          this._updateOptionAvailability(row);
        }
      } else {
        const defaultVariantId = row.querySelector('.add-to-cart-btn')?.dataset.variantId;
        if (defaultVariantId) {
          this._saveStorageData('wishlist_variants', handle, defaultVariantId);
        }
      }
    }

    _updateCustomSelectDisplay(select) {
      const wrapper = select.closest('.custom-select-wrapper');
      if (wrapper) {
        const valueDisplay = wrapper.querySelector('.custom-select__value');
        if (valueDisplay) {
          valueDisplay.textContent = select.value;
        }
      }
    }

    _onVariantChange(select) {
      const row = select.closest('tr.js-wishlist-item');
      if (!row) return;

      this._updateCustomSelectDisplay(select);

      const variantsJson = row.querySelector('.productVariantsJson');
      if (!variantsJson) return;

      const variants = JSON.parse(variantsJson.textContent);
      const selectedOptions = Array.from(row.querySelectorAll('.product-variant-select'))
        .map(s => s.value);

      const matchingVariant = variants.find(variant =>
        selectedOptions.every((opt, idx) => variant.options[idx] === opt)
      );

      if (matchingVariant) {
        this._updateVariantDetails(row, matchingVariant);
        this._updateOptionAvailability(row);
      } else {
        // Inkompatibel variant - treat as out of stock
        const btn = row.querySelector('.add-to-cart-btn');
        if (btn) {
          const btnText = btn.querySelector('span.add-to-cart-text');
          if (btnText) btnText.textContent = 'Ikke på lager';
          
          btn.dataset.available = 'false';
          btn.disabled = true;
          
          row.querySelectorAll('.quantity-selector button, .quantity-selector input')
            .forEach(el => el.disabled = true);
        }
      }
    }

    _updateVariantDetails(row, variant, save = true) {
      row.dataset.basePrice = variant.price;
      const handle = row.dataset.productHandle;

      if (save) {
        this._saveStorageData('wishlist_variants', handle, variant.id);
      }

      const sku = row.querySelector('.product-sku');
      if (sku) sku.textContent = `SKU: ${variant.sku || 'N/A'}`;

      const btn = row.querySelector('.add-to-cart-btn');
      if (btn) {
        btn.dataset.variantId = variant.id;
        const outOfStock = !variant.available || 
          (variant.inventory_quantity !== null && 
          variant.inventory_quantity <= 0 && 
          variant.inventory_policy !== 'continue');

        const btnText = btn.querySelector('span.add-to-cart-text');
        if (btnText) btnText.textContent = outOfStock ? 'Ikke på lager' : 'Legg i handlekurven';
        
        btn.dataset.available = !outOfStock;
        btn.disabled = outOfStock;
        
        row.querySelectorAll('.quantity-selector button, .quantity-selector input')
          .forEach(el => el.disabled = outOfStock);
      }
      
      if (variant.featured_media) {
        const img = row.querySelector('.product-image');
        if (img) img.src = variant.featured_media;
      }

      this._updatePrice(row);
      if (save) this._calculateAndPublishTotal();
    }

    _updateOptionAvailability(row) {
      const variantsJson = row.querySelector('.productVariantsJson');
      if (!variantsJson) return;

      const variants = JSON.parse(variantsJson.textContent);
      const selects = row.querySelectorAll('.product-variant-select');
      const currentSelections = Array.from(selects).map(s => s.value);

      selects.forEach((select, optionIndex) => {
        Array.from(select.options).forEach(option => {
          if (!option.value) {
            option.disabled = false;
            option.style.cssText = 'opacity: 1; pointer-events: auto';
            option.textContent = option.value;
            return;
          }

          const testCombination = [...currentSelections];
          testCombination[optionIndex] = option.value;

          const matchingVariant = variants.find(v =>
            v.options.every((opt, idx) => !testCombination[idx] || opt === testCombination[idx])
          );

          if (matchingVariant) {
            const outOfStock = !matchingVariant.available ||
              (matchingVariant.inventory_quantity !== null &&
               matchingVariant.inventory_quantity <= 0 &&
               matchingVariant.inventory_policy !== 'continue');

            option.style.cssText = `opacity: ${outOfStock ? '0.4' : '1'}; pointer-events: auto`;
            option.textContent = option.value.replace(/ \(Ikke på lager\)| \(Inkompatibel variant\)/g, '') +
              (outOfStock ? ' (Ikke på lager)' : '');
          } else {
            option.style.cssText = 'opacity: 0.4; pointer-events: auto';
            option.textContent = option.value.replace(/ \(Ikke på lager\)| \(Inkompatibel variant\)/g, '') +
              ' (Inkompatibel variant)';
          }
        });
      });
    }

    // Price & Total Calculations
    _updatePrice(row) {
      const display = row.querySelector('.product-price');
      const input = row.querySelector('.quantity-input');
      const basePrice = parseInt(row.dataset.basePrice, 10);

      if (display && input && !isNaN(basePrice)) {
        const total = parseInt(input.value, 10) * basePrice;
        display.innerHTML = this.formatMoney(total);
      }
    }

    _calculateAndPublishTotal() {
      const total = Array.from(this.querySelectorAll('.js-wishlist-item')).reduce((sum, row) => {
        const price = parseInt(row.dataset.basePrice, 10);
        const qty = parseInt(row.querySelector('.quantity-input').value, 10);
        return sum + (isNaN(price) || isNaN(qty) ? 0 : price * qty);
      }, 0);

      window.pubSub?.publish('wishlist:totalUpdated', { total });
    }

    formatMoney(cents) {
      const amount = (cents / 100).toFixed(2);
      const [integer, decimal] = amount.split('.');
      const formatted = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
      return `${formatted}${decimal !== '00' ? ',' + decimal : ''} kr`;
    }

    // Analytics
    pushWishlistViewEvent() {
      if (!this.customerId || !this.customerType) return;
      
      const count = localStorage.getItem('wishlist')?.split('||').filter(Boolean).length || 0;
      window.dataLayer?.push({
        event: 'wishlist-view',
        customer_id: this.customerId,
        page_type: 'wishlist',
        page_path: 'bad.no/pages/favoritter',
        section: 'wishlist-page',
        wishlist_count: count
      });
    }
  }

  customElements.define('wishlist-section', WishlistSection);
}